import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, fmtNum, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, ArrowRight, RotateCcw, Loader2, History, Link2, X, AlertTriangle, FileText, Users, Receipt, Trash2,
} from "lucide-react";
import { toast } from "sonner";

const ACCEPT = ".csv,.tsv,.tab,.txt,.xlsx,.xlsm,.xls,.json,.pdf";

export default function Import() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const fileInput = useRef(null);
  const [dataset, setDataset] = useState("customers"); // customers | transactions
  const [accountType, setAccountType] = useState("auto"); // auto | individual | company
  const [step, setStep] = useState("upload");
  const [files, setFiles] = useState([]);
  const [sheetUrl, setSheetUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const { data: history } = useQuery({ queryKey: ["import-history"], queryFn: () => api.get("/import/history").then((r) => r.data) });

  const clearHistory = async () => {
    try {
      await api.delete("/import/history");
      qc.invalidateQueries({ queryKey: ["import-history"] });
      toast.success("Import history cleared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => f.name + f.size));
      return [...prev, ...incoming.filter((f) => !seen.has(f.name + f.size))];
    });
  };
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const buildForm = (withMapping) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (sheetUrl.trim()) fd.append("sheet_url", sheetUrl.trim());
    fd.append("account_type", accountType);
    if (withMapping) fd.append("mapping", JSON.stringify(mapping));
    return fd;
  };

  const switchDataset = (d) => { setDataset(d); setStep("upload"); setFiles([]); setSheetUrl(""); setPreview(null); setResult(null); setAccountType("auto"); };

  const analyze = async () => {
    if (files.length === 0 && !sheetUrl.trim()) {
      toast.error("Add at least one file or a link");
      return;
    }
    setBusy(true);
    try {
      if (dataset === "transactions") {
        const { data } = await api.post("/transactions/import", buildForm(false));
        setResult(data);
        setStep("done");
        qc.invalidateQueries();
        toast.success("Transactions imported");
        return;
      }
      const { data } = await api.post("/import/preview", buildForm(false));
      setPreview(data);
      setMapping(data.suggested_mapping);
      setStep("map");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not read the data");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", buildForm(true));
      setResult(data);
      setStep("done");
      qc.invalidateQueries();
      toast.success("Import complete");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const restart = () => { setStep("upload"); setFiles([]); setSheetUrl(""); setPreview(null); setMapping({}); setResult(null); };

  const fieldOptions = [
    { value: "__custom__", label: "Custom field (keep as-is)" },
    { value: "__skip__", label: "— Do not import —" },
    ...(preview?.canonical_fields || []).map((f) => ({ value: f.key, label: f.label })),
  ];
  const unknownCount = preview ? preview.columns.filter((c) => mapping[c] === "__custom__").length : 0;
  const isTx = dataset === "transactions";

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Import Data</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isTx
            ? "Upload your transactions export (auto-mapped by column name). Used to calculate bank costs, affiliate commissions and net profit."
            : "Upload multiple files (CSV, Excel, TSV, JSON, PDF) or paste a link. Duplicates are updated by User ID."}
        </p>
      </div>

      {/* Dataset toggle */}
      <div className="inline-flex rounded-md border border-border bg-card p-1" data-testid="dataset-toggle">
        {[{ k: "customers", label: "Customers", icon: Users }, { k: "transactions", label: "Transactions", icon: Receipt }].map((d) => (
          <button
            key={d.k}
            onClick={() => switchDataset(d.k)}
            data-testid={`dataset-${d.k}`}
            className={`flex items-center gap-2 rounded px-4 py-2 text-sm font-medium transition-colors ${
              dataset === d.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <d.icon className="h-4 w-4" /> {d.label}
          </button>
        ))}
      </div>

      {/* Stepper (customers only) */}
      {!isTx && (
        <div className="flex items-center gap-3 text-sm">
          {["Add sources", "Map fields", "Done"].map((s, i) => {
            const active = ["upload", "map", "done"].indexOf(step) >= i;
            return (
              <div key={s} className="flex items-center gap-3">
                <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{i + 1}</span>
                <span className={active ? "font-medium text-foreground" : "text-muted-foreground"}>{s}</span>
                {i < 2 && <span className="h-px w-8 bg-border" />}
              </div>
            );
          })}
        </div>
      )}

      {step === "upload" && (
        <div className="space-y-4">
          <div
            className="flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-border bg-card px-6 py-12 text-center transition-colors hover:border-[#2b9bb7] hover:bg-[#EDF3F5]"
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            data-testid="upload-dropzone"
          >
            <input ref={fileInput} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="file-input" />
            <UploadCloud className="h-10 w-10 text-[#2b9bb7]" />
            <p className="mt-4 font-heading text-lg font-medium">Drop files here or click to browse</p>
            <p className="mt-1 text-sm text-muted-foreground">CSV · Excel · TSV · JSON · PDF</p>
          </div>

          {files.length > 0 && (
            <div className="space-y-2 rounded-md border border-border bg-card p-4" data-testid="selected-files">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{files.length} file(s) selected</div>
              {files.map((f, i) => (
                <div key={f.name + i} className="flex items-center justify-between rounded-md bg-[#EDF3F5] px-3 py-2 text-sm" data-testid={`selected-file-${i}`}>
                  <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#2b9bb7]" /> {f.name}</span>
                  <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-file-${i}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-md border border-border bg-card p-4">
            <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Link2 className="h-4 w-4" /> Or paste a public Google Sheet / CSV link
            </label>
            <Input value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="sheet-url-input" />
          </div>

          {!isTx && (
            <div className="rounded-md border border-border bg-card p-4" data-testid="account-type-picker">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Report type</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "auto", label: "Auto-detect" },
                  { k: "individual", label: "User report (Individuals)" },
                  { k: "company", label: "Company report (Corporate)" },
                ].map((o) => (
                  <button
                    key={o.k}
                    onClick={() => setAccountType(o.k)}
                    data-testid={`account-type-${o.k}`}
                    className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                      accountType === o.k ? "border-[#2b9bb7] bg-[#2b9bb7]/10 text-[#2b9bb7]" : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Auto-detect recognizes company exports by their "Company ID" / KYB columns.</p>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={analyze} disabled={busy} data-testid="analyze-button">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              {isTx ? "Import transactions" : "Analyze sources"}
            </Button>
          </div>
        </div>
      )}

      {step === "map" && preview && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[#2b9bb7]" />
              <span className="text-sm font-medium">{fmtNum(preview.total_rows)} rows from {preview.files.length} source(s)</span>
              {preview.files.map((f, i) => (
                <span key={i} className="rounded bg-[#EDF3F5] px-2 py-0.5 text-xs text-muted-foreground" data-testid={`preview-file-${i}`}>
                  {f.name} · {fmtNum(f.rows)}
                  {f.detected_type && (
                    <span className={`ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${f.detected_type === "company" ? "bg-[#5B6ABF]/15 text-[#4A57A3]" : "bg-[#2b9bb7]/15 text-[#22798f]"}`}>
                      {f.detected_type === "company" ? "Corporate" : "Individual"}
                    </span>
                  )}
                </span>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={restart} data-testid="cancel-import">Cancel</Button>
          </div>

          {unknownCount > 0 && (
            <div className="flex items-center gap-2 rounded-md bg-[#D68C3C]/10 px-4 py-2.5 text-sm text-[#A7773F]" data-testid="unknown-warning">
              <AlertTriangle className="h-4 w-4" />
              {unknownCount} column(s) weren't recognized. Review them below — they'll be stored as custom fields unless you map them.
            </div>
          )}

          <div className="overflow-hidden rounded-md border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-[#EDF3F5] text-left">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Source Column</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sample</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Import as</th>
                </tr>
              </thead>
              <tbody>
                {preview.columns.map((col) => {
                  const isUnknown = mapping[col] === "__custom__";
                  return (
                    <tr key={col} className="border-b border-border" data-testid={`map-row-${col}`}>
                      <td className="px-4 py-2.5 font-medium">
                        <span className="flex items-center gap-2">
                          {col}
                          {isUnknown && <span className="rounded bg-[#D68C3C]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#A7773F]">review</span>}
                        </span>
                      </td>
                      <td className="max-w-[240px] truncate px-4 py-2.5 font-mono text-xs text-muted-foreground">{String(preview.sample_rows?.[0]?.[col] ?? "")}</td>
                      <td className="px-4 py-2.5">
                        <select
                          value={mapping[col] || "__custom__"}
                          onChange={(e) => setMapping((m) => ({ ...m, [col]: e.target.value }))}
                          data-testid={`map-select-${col}`}
                          className="h-10 w-full max-w-xs rounded-md border border-input bg-card px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring"
                        >
                          {fieldOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <Button onClick={commit} disabled={busy} data-testid="confirm-import">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              Import {fmtNum(preview.total_rows)} rows
            </Button>
          </div>
        </div>
      )}

      {step === "done" && result && (
        <div className="rounded-md border border-border bg-card p-8" data-testid="import-result">
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#326F4B]" />
            <h3 className="mt-4 font-heading text-xl font-semibold">{isTx ? "Transactions imported" : "Import complete"}</h3>
          </div>
          <div className="mx-auto mt-6 grid max-w-lg grid-cols-3 gap-4">
            <div className="rounded-md bg-[#EDF3F5] p-4 text-center"><div className="font-heading text-2xl font-semibold text-[#326F4B]">{fmtNum(result.created)}</div><div className="text-xs uppercase tracking-wider text-muted-foreground">New</div></div>
            <div className="rounded-md bg-[#EDF3F5] p-4 text-center"><div className="font-heading text-2xl font-semibold text-[#2b9bb7]">{fmtNum(result.updated)}</div><div className="text-xs uppercase tracking-wider text-muted-foreground">Updated</div></div>
            <div className="rounded-md bg-[#EDF3F5] p-4 text-center"><div className="font-heading text-2xl font-semibold">{fmtNum(result.total)}</div><div className="text-xs uppercase tracking-wider text-muted-foreground">Total</div></div>
          </div>
          {result.protected_kept > 0 && (
            <p className="mx-auto mt-4 max-w-lg rounded-md bg-[#326F4B]/8 px-4 py-2.5 text-center text-sm text-[#326F4B]" data-testid="protected-kept-note">
              🔒 {fmtNum(result.protected_kept)} protected value(s) kept intact — corrections made in the portal were not overwritten.
            </p>
          )}
          <div className="mt-6 text-center">
            <Button onClick={restart} data-testid="import-another"><RotateCcw className="mr-2 h-4 w-4" /> Import more</Button>
          </div>
        </div>
      )}

      {history && history.length > 0 && (
        <div className="rounded-md border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><History className="h-4 w-4" /> Recent imports</div>
            {isAdmin && (
              <button onClick={clearHistory} className="flex items-center gap-1 text-xs text-destructive hover:underline" data-testid="clear-import-history">
                <Trash2 className="h-3 w-3" /> Clear history
              </button>
            )}
          </div>
          <div className="divide-y divide-border">
            {history.slice(0, 6).map((h, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 text-sm" data-testid={`history-row-${i}`}>
                <span className="flex items-center gap-2 font-medium">
                  {h.kind === "transactions" ? <Receipt className="h-3.5 w-3.5 text-[#2b9bb7]" /> : <Users className="h-3.5 w-3.5 text-[#2b9bb7]" />}
                  {h.filename}
                </span>
                <div className="flex items-center gap-5 text-xs text-muted-foreground">
                  <span>+{fmtNum(h.created)} new</span>
                  <span>{fmtNum(h.updated)} updated</span>
                  <span>{fmtDate(h.at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
